import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Icon } from "../../ui/kit";
import { topBarButton } from "../../ui/editor/TopBar";
import { OPEN_GOLDSMITH_EVENT } from "../../ui/editor/basic/events";
import { OPEN_CUSTOMER_LINK_EVENT } from "../../ui/editor/configurator/draft";
import type { ViewerClient } from "../../client/ViewerClient";
import { listModels, getModel, type VaultModel } from "../../features/vault/vaultApi";
import { getScopeSettings, saveScopeSettings, type RenderValues } from "../../features/rendersettings/renderSettingsApi";
import { useToast } from "../../shared/ui";
import { SendToGoldsmithDialog } from "../../features/goldsmith/SendToGoldsmithDialog";

/** OUTPUT label → pixel dims. "Custom" reads the renderer's own size instead. */
const RES_DIMS: Record<string, [number, number]> = {
  "1080p": [1920, 1080],
  "2K": [2560, 1440],
  "4K": [3840, 2160],
  "8K": [7680, 4320],
};
const RES_OPTIONS = ["1080p", "2K", "4K", "8K", "Custom"] as const;
type Res = (typeof RES_OPTIONS)[number];

const menuItem =
  "flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-ed-text hover:bg-ed-hover disabled:cursor-not-allowed disabled:opacity-40";

/** "← Vault", the way out of the studio. Only when the studio was opened from
 *  the Vault — /dev/studio has nowhere to go back to. */
export function StudioTopBarLeading({ modelId }: { modelId: string | undefined }) {
  if (!modelId) return null;
  return (
    <Link to="/vault" className={`${topBarButton} -ml-1 px-2 text-ed-text-2`} title="Back to the Vault">
      <Icon name="arrow_back" size={18} />
      Vault
    </Link>
  );
}

/**
 * The Vault-bound actions that live on the studio's top bar.
 *
 * These were a pill row floating over the canvas at `top-11`, which stopped
 * working the moment the canvas became a card with a margin — the row sat on
 * it. Everything here is Vault-coupled (modelId, listModels, folder scope,
 * /bulk), which is why it lives in app/pages rather than in ui/editor: the top
 * bar takes it as an opaque node and knows nothing about any of it.
 *
 * ⚠️ getScopeSettings and saveScopeSettings MUST stay in the same file.
 * scripts/check-server-logic.ts asserts that every file calling the setter also
 * calls the getter, because writing the five keys this owns without merging
 * over the node first DELETES the ~20 the Render Settings page owns on it.
 */
export function StudioTopBarActions({
  client,
  modelId,
  hint,
  saving,
  saved,
  thumbState,
  onSave,
  onThumbnail,
  onOpenConfigurator,
}: {
  client: ViewerClient | null;
  modelId: string | undefined;
  /** Why the model-bound actions can't run yet, or undefined when they can. */
  hint?: string;
  saving: boolean;
  saved: boolean;
  thumbState: "idle" | "busy" | "done";
  onSave: () => void | Promise<unknown>;
  onThumbnail: () => void;
  onOpenConfigurator: () => void;
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [models, setModels] = useState<VaultModel[]>([]);
  const [res, setRes] = useState<Res>("1080p");
  const [moreOpen, setMoreOpen] = useState(false);
  const [goldsmithOpen, setGoldsmithOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!modelId) return;
    void listModels(null, { recent: true })
      .then((ms) => setModels(ms.filter((m) => m.status === "ready")))
      .catch(() => setModels([]));
  }, [modelId]);

  // The Basic Export panel's "AI Photoshoot" row asks for this dialog. The
  // editor knows nothing about the Vault, so it asks by event, not by import.
  useEffect(() => {
    const open = () => setGoldsmithOpen(true);
    window.addEventListener(OPEN_GOLDSMITH_EVENT, open);
    return () => window.removeEventListener(OPEN_GOLDSMITH_EVENT, open);
  }, []);

  useEffect(() => {
    if (!moreOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMoreOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  /**
   * OUTPUT writes the RENDERER's size rather than keeping a size of its own.
   * useExportSize already makes both export panels follow getRendererParams, and
   * its own doc comment records that three competing size controls was the
   * original bug — so this joins that one source instead of adding a third.
   */
  const pickRes = (v: Res) => {
    setRes(v);
    const dims = RES_DIMS[v];
    if (!dims || !client) return;
    void client.ring.setRendererParams({ mode: "custom", width: dims[0], height: dims[1] });
  };

  const saveScope = async (scope: "folder" | "global") => {
    setMoreOpen(false);
    const values: RenderValues = {};
    // `resolution` is resolved by LABEL against a fixed table (BrowserRenderRunner's
    // RESOLUTION_PX). "Custom" is not a key in it, so saving one would silently
    // drop every batch render at this scope to the fallback size — leave
    // whatever is inherited alone instead.
    if (res !== "Custom") values.resolution = res;
    try {
      let scopeId: string | null = null;
      if (scope === "folder") {
        if (!modelId) { toast({ tone: "warning", title: "Open a model to set a folder default" }); return; }
        const m = await getModel(modelId);
        if (!m.folderId) {
          toast({
            tone: "warning",
            title: "This model is at the vault root",
            description: "Move it into a folder to save folder-scoped render settings.",
          });
          return;
        }
        scopeId = m.folderId;
      }
      // Read-modify-write. The server PUT $sets the whole `values` subdocument —
      // correct for the Render Settings page, which always writes its complete
      // override set, and wrong for us: writing ours alone would delete theirs.
      const current = await getScopeSettings(scope, scopeId).catch(() => ({} as RenderValues));
      await saveScopeSettings(scope, scopeId, { ...current, ...values });
      toast({
        tone: "success",
        title: scope === "folder" ? "Saved as the folder default" : "Saved as the global default",
        description: res === "Custom" ? "Custom sizes aren’t saved as a default — the inherited output size was kept." : undefined,
      });
    } catch {
      toast({ tone: "error", title: "Could not save render settings" });
    }
  };

  /**
   * The bar keeps only what a jeweller reaches for: AI Photoshoot, Save, and
   * the Export button the editor adds after this. Everything Vault-specific
   * and occasional — thumbnail, configurator, another model, batch render and
   * its size defaults — lives one click away under More, instead of as seven
   * pills and a sentence of grey hint text across the top of the screen.
   */
  return (
    <>
      {/* Available with or without a Vault model: a dropped file can be sent too. */}
      <button
        onClick={() => setGoldsmithOpen(true)}
        title="Send this piece to TheGoldsmith for an AI photoshoot or short video"
        className={topBarButton}
        data-goldsmith-open
      >
        <Icon name="auto_awesome" size={18} />
        <span className="max-[1100px]:hidden">AI Photoshoot</span>
      </button>
      <SendToGoldsmithDialog open={goldsmithOpen} onClose={() => setGoldsmithOpen(false)} client={client} />

      <button
        onClick={() => void onSave()}
        disabled={!modelId || !!hint || saving}
        title={!modelId ? "Open a model from the Vault to save its scene" : hint ?? "Save the current scene (lighting, materials, background) onto this model"}
        className={`${topBarButton} ${modelId ? "" : "max-[700px]:hidden"}`}
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : null}
        {saving ? "Saving…" : saved ? "Saved" : "Save"}
      </button>

      {modelId ? (
        <div ref={popRef} className="relative">
          <button
            onClick={() => setMoreOpen((v) => !v)}
            className={`${topBarButton} px-2`}
            title="More"
            aria-label="More"
            aria-expanded={moreOpen}
          >
            <Icon name="more_horiz" size={20} />
          </button>
          {moreOpen ? (
            <div className="absolute right-0 top-full z-50 mt-1.5 w-64 overflow-hidden rounded-xl border border-ed-border bg-ed-panel py-1.5 shadow-[0_6px_20px_rgba(0,0,0,0.08)]">
              <button
                className={menuItem}
                disabled={!!hint || thumbState === "busy"}
                title={hint}
                onClick={() => { setMoreOpen(false); onThumbnail(); }}
              >
                <Icon name="photo" size={18} className="text-ed-text-2" />
                {thumbState === "busy" ? "Updating thumbnail…" : thumbState === "done" ? "Thumbnail updated" : "Use this view as the thumbnail"}
              </button>
              <button
                className={menuItem}
                disabled={!!hint}
                title={hint}
                onClick={() => { setMoreOpen(false); onOpenConfigurator(); }}
              >
                <Icon name="tune" size={18} className="text-ed-text-2" />
                Customer configurator…
              </button>
              <button
                className={menuItem}
                disabled={!!hint}
                title={hint ?? "Open the link shoppers get, in a new tab (saves your customer choices first)"}
                // The draft lives inside the editor; it saves and opens the
                // tab. Dispatched synchronously, so the tab is this click's.
                onClick={() => { setMoreOpen(false); window.dispatchEvent(new Event(OPEN_CUSTOMER_LINK_EVENT)); }}
                data-open-customer-link
              >
                <Icon name="open_in_new" size={18} className="text-ed-text-2" />
                Open customer link
              </button>
              {models.length > 1 ? (
                <>
                  <div className="mx-3 my-1.5 border-t border-ed-border" />
                  <div className="px-3 pb-1 text-[12px] font-semibold text-ed-text-2">Open another model</div>
                  <div className="max-h-48 overflow-y-auto">
                    {/* ⚠️ A hard reload, deliberately: ViewerHost holds ONE
                        long-lived iframe and StudioModelLoader never tears the
                        old model down, so a soft navigate would leave the
                        previous model's materials and stage props behind. */}
                    {models.map((m) => (
                      <button
                        key={m._id}
                        className={`${menuItem} ${m._id === modelId ? "font-semibold" : ""}`}
                        onClick={() => { if (m._id !== modelId) window.location.assign(`/studio/${m._id}`); }}
                      >
                        <span className="truncate">{m.name}</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
              <div className="mx-3 my-1.5 border-t border-ed-border" />
              <div className="flex items-center justify-between gap-2 px-3 py-1.5">
                <span className="text-[13px] text-ed-text">Batch render size</span>
                <select
                  value={res}
                  onChange={(e) => pickRes(e.target.value as Res)}
                  className="h-7 rounded-md border border-ed-line bg-ed-panel px-1.5 text-[13px] text-ed-text"
                >
                  {RES_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <button
                className={menuItem}
                onClick={() => {
                  setMoreOpen(false);
                  // Only the labels /bulk can resolve are handed over —
                  // "1080p" and "Custom" are not keys in its resolution table.
                  navigate("/bulk", { state: ["2K", "4K", "8K"].includes(res) ? { resolution: res } : undefined });
                }}
              >
                <Icon name="burst_mode" size={18} className="text-ed-text-2" />
                Batch render {res}…
              </button>
              <button className={menuItem} onClick={() => void saveScope("folder")}>
                <Icon name="folder" size={18} className="text-ed-text-2" />
                Use this size for this folder
              </button>
              <button className={menuItem} onClick={() => void saveScope("global")}>
                <Icon name="public" size={18} className="text-ed-text-2" />
                Use this size everywhere
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
